import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NotificationBell from '../components/NotificationBell';
import DeviceManager from '../components/DeviceManager';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Activity, Heart, Brain, Zap, TrendingUp, Target,
  Droplets, Moon, Footprints, Watch, AlertTriangle, CheckCircle2,
  Clock, Sparkles, Shield, Flame, Wind, RefreshCw,
  Plus, X, Loader2, ArrowRight, Scale, Download, Mail,
  Upload, FileText, Trash2
} from 'lucide-react';
import { programData } from '../data/healthData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem('annalise_session_id');
  if (!id) {
    id = `annalise_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('annalise_session_id', id);
  }
  return id;
};

// ── Vitality Score Circle ──
const VitalityScoreCircle = ({ score, size = 200 }) => {
  const radius = (size - 20) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const getColor = (s) => s >= 80 ? '#22C55E' : s >= 60 ? '#F59E0B' : '#EF4444';
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#E5E7EB" strokeWidth="12" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={getColor(score)}
          strokeWidth="12" strokeLinecap="round" strokeDasharray={circumference}
          strokeDashoffset={circumference - progress} className="transition-all duration-1000 ease-out" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-5xl font-bold text-gray-900" data-testid="vitality-score-value">{score}</span>
        <span className="text-sm text-gray-500">Vitality Score</span>
      </div>
    </div>
  );
};

// ── Risk Radar ──
const RiskRadarChart = ({ data }) => {
  const cats = [
    { key: 'metabolic', label: 'Metabolic', icon: Flame },
    { key: 'stressRecovery', label: 'Stress/Recovery', icon: Wind },
    { key: 'inflammation', label: 'Inflammation', icon: Zap },
    { key: 'lifestyle', label: 'Lifestyle', icon: Heart }
  ];
  const riskColor = (r) => r === 'low' ? 'bg-green-100 text-green-700 border-green-200' :
    r === 'moderate' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' : 'bg-red-100 text-red-700 border-red-200';
  return (
    <div className="grid grid-cols-2 gap-4">
      {cats.map(({ key, label, icon: Icon }) => {
        const rd = data[key];
        return (
          <div key={key} className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center"><Icon className="w-4 h-4 text-gray-600" /></div>
              <span className="font-medium text-gray-900 text-sm">{label}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold text-gray-900">{rd.score}</span>
              <span className={`text-xs px-2 py-1 rounded-full border capitalize ${riskColor(rd.risk)}`}>{rd.risk} risk</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Mini Chart ──
const MiniChart = ({ data, color = '#166534', height = 40 }) => {
  const max = Math.max(...data); const min = Math.min(...data); const range = max - min || 1;
  return (
    <div className="flex items-end gap-1" style={{ height }}>
      {data.map((v, i) => (
        <div key={i} className="flex-1 rounded-t transition-all hover:opacity-80"
          style={{ height: `${((v - min) / range) * 100}%`, minHeight: '4px', backgroundColor: color }} />
      ))}
    </div>
  );
};

// ── Guided Tour ──
const GuidedTour = ({ onDismiss }) => {
  const [step, setStep] = useState(0);
  const steps = [
    { title: 'Welcome to Your Health Dashboard', description: 'Your personalized command center for tracking health metrics, vitality scores, and AI-powered insights.', icon: Sparkles },
    { title: 'Vitality Score', description: 'Your overall health score calculated from sleep, activity, nutrition, recovery, and stress. Log data regularly to see it update!', icon: Activity },
    { title: 'Quick Stats', description: 'At-a-glance metrics from your latest health entries — heart rate, steps, sleep, hydration, and more.', icon: Heart },
    { title: 'AI Digital Twin', description: 'Predictive health modeling that simulates how lifestyle changes could impact your health trajectory.', icon: Brain },
    { title: 'Log Health Data', description: 'Use the "Log Health Data" button in the header or the Annalise chat to manually enter your daily metrics anytime.', icon: Plus },
  ];
  const s = steps[step]; const Icon = s.icon;
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="guided-tour-overlay">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl">
        <div className="flex justify-end mb-2">
          <button onClick={onDismiss} className="text-gray-400 hover:text-gray-600 text-sm" data-testid="tour-skip-btn">Skip tour</button>
        </div>
        <div className="w-16 h-16 bg-gradient-to-br from-[#22c55e] to-[#166534] rounded-2xl flex items-center justify-center mx-auto mb-6">
          <Icon className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 text-center mb-3">{s.title}</h3>
        <p className="text-gray-600 text-center mb-8 leading-relaxed">{s.description}</p>
        <div className="flex items-center justify-between">
          <div className="flex gap-1.5">{steps.map((_, i) => (<div key={i} className={`w-2 h-2 rounded-full transition-all ${i === step ? 'bg-[#166534] w-6' : 'bg-gray-200'}`} />))}</div>
          <div className="flex gap-2">
            {step > 0 && <Button variant="outline" size="sm" onClick={() => setStep(step - 1)}>Back</Button>}
            {step < steps.length - 1 ? (
              <Button size="sm" className="bg-[#166534] hover:bg-[#14532d] text-white gap-1" onClick={() => setStep(step + 1)} data-testid="tour-next-btn">Next <ArrowRight className="w-4 h-4" /></Button>
            ) : (
              <Button size="sm" className="bg-[#166534] hover:bg-[#14532d] text-white" onClick={onDismiss} data-testid="tour-finish-btn">Get Started</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Return Visit Prompt ──
const ReturnVisitPrompt = ({ onLogData, onSyncDevices, onDismiss }) => (
  <div className="bg-gradient-to-r from-[#166534] to-[#22c55e] rounded-2xl p-6 mb-8 text-white relative" data-testid="return-visit-prompt">
    <button onClick={onDismiss} className="absolute top-4 right-4 text-white/70 hover:text-white" data-testid="dismiss-prompt-btn"><X className="w-5 h-5" /></button>
    <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
      <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0"><RefreshCw className="w-7 h-7 text-white" /></div>
      <div className="flex-1">
        <h3 className="text-lg font-bold mb-1">Welcome back! Time for your daily check-in</h3>
        <p className="text-green-100 text-sm">Keep your health data up to date for the most accurate insights and predictions.</p>
      </div>
      <div className="flex gap-3 flex-shrink-0">
        <Button onClick={onLogData} className="bg-white text-[#166534] hover:bg-green-50 font-medium gap-2" data-testid="prompt-log-data-btn"><Plus className="w-4 h-4" /> Update Health Stats</Button>
        <Button onClick={onSyncDevices} variant="outline" className="border-white/40 text-white hover:bg-white/10 font-medium gap-2" data-testid="prompt-sync-btn"><Watch className="w-4 h-4" /> Sync Wearables</Button>
      </div>
    </div>
  </div>
);

// ── Weekly Summary Banner ──
const WeeklySummary = ({ data }) => {
  if (!data?.has_data) return null;
  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm mb-8" data-testid="weekly-summary">
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><Target className="w-5 h-5 text-[#166534]" /> Weekly Summary ({data.days_logged} days logged)</h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {data.avg_sleep != null && (
          <div className="text-center p-3 bg-purple-50 rounded-xl">
            <Moon className="w-5 h-5 text-purple-500 mx-auto mb-1" />
            <div className="text-xl font-bold text-gray-900">{data.avg_sleep}h</div>
            <div className="text-xs text-gray-500">Avg Sleep</div>
          </div>
        )}
        {data.avg_steps != null && (
          <div className="text-center p-3 bg-green-50 rounded-xl">
            <Footprints className="w-5 h-5 text-green-500 mx-auto mb-1" />
            <div className="text-xl font-bold text-gray-900">{data.avg_steps.toLocaleString()}</div>
            <div className="text-xs text-gray-500">Avg Steps</div>
          </div>
        )}
        {data.avg_water != null && (
          <div className="text-center p-3 bg-blue-50 rounded-xl">
            <Droplets className="w-5 h-5 text-blue-500 mx-auto mb-1" />
            <div className="text-xl font-bold text-gray-900">{data.avg_water}</div>
            <div className="text-xs text-gray-500">Avg Water</div>
          </div>
        )}
        {data.avg_stress != null && (
          <div className="text-center p-3 bg-red-50 rounded-xl">
            <Brain className="w-5 h-5 text-red-400 mx-auto mb-1" />
            <div className="text-xl font-bold text-gray-900">{data.avg_stress}/10</div>
            <div className="text-xs text-gray-500">Avg Stress</div>
          </div>
        )}
        {data.weight_change != null && (
          <div className="text-center p-3 bg-amber-50 rounded-xl">
            <Scale className="w-5 h-5 text-amber-500 mx-auto mb-1" />
            <div className="text-xl font-bold text-gray-900">{data.weight_change > 0 ? '+' : ''}{data.weight_change} lbs</div>
            <div className="text-xs text-gray-500">Weight Change</div>
          </div>
        )}
      </div>
    </div>
  );
};


// ── Connected Devices Bar (dynamic) ──
const DevicesBar = ({ onManage }) => {
  const [devices, setDevices] = useState([]);
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const res = await fetch(`${API}/devices/${getSessionId()}`);
        const data = await res.json();
        setDevices(data.devices || []);
      } catch {}
    };
    fetchDevices();
    const handler = () => fetchDevices();
    window.addEventListener('healthDataUpdated', handler);
    return () => window.removeEventListener('healthDataUpdated', handler);
  }, []);

  const connected = devices.filter(d => d.connected);

  return (
    <div className="bg-white rounded-2xl p-4 mb-8 border border-gray-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <span className="text-sm font-medium text-gray-500">Connected Devices:</span>
          {connected.length === 0 ? (
            <span className="text-sm text-gray-400">None connected yet</span>
          ) : connected.map(d => (
            <div key={d.device_type} className="flex items-center gap-2">
              <Watch className="w-4 h-4 text-green-600" />
              <span className="text-sm text-gray-700">{d.name}</span>
              <span className="w-2 h-2 bg-green-500 rounded-full" />
            </div>
          ))}
        </div>
        <Button variant="ghost" size="sm" className="text-[#166534]" onClick={onManage} data-testid="add-device-btn">
          <Plus className="w-4 h-4 mr-1" /> {connected.length > 0 ? 'Manage Devices' : 'Connect Device'}
        </Button>
      </div>
    </div>
  );
};


// ── Lab Results Tab ──
const LabResultsTab = () => {
  const [labs, setLabs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [testName, setTestName] = useState('');
  const [testDate, setTestDate] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const fetchLabs = async () => {
    try {
      const res = await fetch(`${API}/lab-results/${getSessionId()}`);
      const data = await res.json();
      setLabs(data.lab_results || []);
    } catch {}
  };

  useEffect(() => { fetchLabs(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !testName) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_session', getSessionId());
    formData.append('test_name', testName);
    formData.append('test_date', testDate || new Date().toISOString().split('T')[0]);
    formData.append('notes', notes);
    try {
      await fetch(`${API}/lab-results/upload`, { method: 'POST', body: formData });
      setShowUpload(false);
      setTestName(''); setTestDate(''); setNotes(''); setFile(null);
      fetchLabs();
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const deleteLab = async (id) => {
    try {
      await fetch(`${API}/lab-results/${id}`, { method: 'DELETE' });
      fetchLabs();
    } catch {}
  };

  const sendEmailSummary = async () => {
    const email = prompt('Enter email address to send weekly summary:');
    if (!email) return;
    setEmailSending(true);
    try {
      const res = await fetch(`${API}/email-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_session: getSessionId(), recipient_email: email })
      });
      const data = await res.json();
      setEmailResult(data.message);
      setTimeout(() => setEmailResult(null), 5000);
    } catch (err) {
      setEmailResult('Failed to send email');
      setTimeout(() => setEmailResult(null), 3000);
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-8 border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Lab Results & Reports</h2>
            <p className="text-gray-500 mt-1">Upload lab results and export health reports</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="gap-2" onClick={sendEmailSummary} disabled={emailSending} data-testid="email-summary-btn">
              <Mail className="w-4 h-4" /> {emailSending ? 'Sending...' : 'Email Summary'}
            </Button>
            <Button className="bg-[#166534] hover:bg-[#14532d] text-white gap-2" onClick={() => setShowUpload(true)} data-testid="upload-lab-btn">
              <Upload className="w-4 h-4" /> Upload Lab Result
            </Button>
          </div>
        </div>

        {emailResult && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-xl mb-4 text-sm text-green-700 flex items-center gap-2" data-testid="email-result-msg">
            <CheckCircle2 className="w-4 h-4" /> {emailResult}
          </div>
        )}

        {/* Upload Form */}
        {showUpload && (
          <div className="p-6 bg-gray-50 rounded-xl border border-gray-200 mb-6" data-testid="upload-form">
            <h3 className="font-semibold text-gray-900 mb-4">Upload Lab Result</h3>
            <form onSubmit={handleUpload} className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Test Name *</label>
                <input type="text" value={testName} onChange={e => setTestName(e.target.value)} required
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="e.g. Blood Panel, Lipid Profile"
                  data-testid="lab-test-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Test Date</label>
                <input type="date" value={testDate} onChange={e => setTestDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700 block mb-1">File (PDF, JPG, PNG) *</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => setFile(e.target.files[0])}
                  className="w-full text-sm" data-testid="lab-file-input" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700 block mb-1">Notes</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none h-16" placeholder="Optional notes..." />
              </div>
              <div className="md:col-span-2 flex gap-3">
                <Button type="button" variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
                <Button type="submit" disabled={uploading || !file || !testName} className="bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="submit-lab-upload">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Upload
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Lab Results List */}
        {labs.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No lab results uploaded yet</p>
            <p className="text-sm mt-1">Upload your lab results to keep all your health data in one place.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="lab-results-list">
            {labs.map(lab => (
              <div key={lab.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-[#166534] transition-all">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm">{lab.test_name}</p>
                  <p className="text-xs text-gray-500">
                    {lab.test_date} &bull; {lab.original_name} &bull; {(lab.file_size / 1024).toFixed(0)} KB
                  </p>
                  {lab.notes && <p className="text-xs text-gray-400 mt-0.5">{lab.notes}</p>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => deleteLab(lab.id)} className="text-gray-400 hover:text-red-500" data-testid={`delete-lab-${lab.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


// ══════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════

const HealthDashboardPage = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTour, setShowTour] = useState(false);
  const [showReturnPrompt, setShowReturnPrompt] = useState(false);
  const [showDeviceManager, setShowDeviceManager] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/health-dashboard/${getSessionId()}`);
      const data = await res.json();
      setDashboardData(data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    const tourDone = localStorage.getItem('health_dashboard_tour_done');
    if (!tourDone) setShowTour(true);
    else setShowReturnPrompt(true);
  }, [fetchDashboard]);

  // Listen for health data updates → immediate refresh
  useEffect(() => {
    const handler = () => { fetchDashboard(); };
    window.addEventListener('healthDataUpdated', handler);
    return () => window.removeEventListener('healthDataUpdated', handler);
  }, [fetchDashboard]);

  const dismissTour = () => { setShowTour(false); localStorage.setItem('health_dashboard_tour_done', 'true'); setShowReturnPrompt(true); };

  const d = dashboardData;
  const hasData = d?.has_data;
  const vs = d?.vitality_score || { current: 50, trend: '+0', subscores: [{ name: 'Sleep', score: 50, trend: '+0', color: '#8B5CF6' }, { name: 'Activity', score: 50, trend: '+0', color: '#22C55E' }, { name: 'Nutrition', score: 50, trend: '+0', color: '#F59E0B' }, { name: 'Recovery', score: 50, trend: '+0', color: '#3B82F6' }, { name: 'Stress', score: 50, trend: '+0', color: '#EF4444' }], history: [] };
  const ba = d?.biological_age || { chronological_age: 35, biological_age: 35, difference: 0, longevity_score: 50, trend: 'unknown', history: [] };
  const rr = d?.health_risk_radar || { metabolic: { risk: 'moderate', score: 70, factors: ['Log data for assessment'] }, stressRecovery: { risk: 'moderate', score: 65, factors: ['Log data'] }, inflammation: { risk: 'moderate', score: 75, factors: ['Log data'] }, lifestyle: { risk: 'moderate', score: 70, factors: ['Log data'] } };
  const ins = d?.ai_insights || { alerts: [], coaching_insights: [], daily_health_plan: [] };
  const preds = d?.digital_twin_predictions || [];
  const qs = d?.quick_stats || {};
  const ws = d?.weekly_summary || {};

  // Fallback display values for quick stats
  const hr = qs.heart_rate ?? '--';
  const steps = qs.steps != null ? qs.steps.toLocaleString() : '--';
  const sleepH = qs.sleep_hours ?? '--';
  const sleepQ = qs.sleep_quality ?? '--';
  const water = qs.water_glasses ?? '--';
  const stress = qs.stress_level ?? '--';
  const energy = qs.energy_level ?? '--';
  const mood = qs.mood ?? '--';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {showTour && <GuidedTour onDismiss={dismissTour} />}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="health-dashboard-title">Health Dashboard</h1>
            <p className="text-gray-600">{hasData ? 'Your personalized health insights from logged data' : 'Log health data to see personalized insights'}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" className="gap-2" onClick={() => window.open(`${API}/health-report/${getSessionId()}`, '_blank')} data-testid="download-pdf-btn">
              <Download className="w-4 h-4" /> PDF Report
            </Button>
            <Button variant="outline" size="sm" className="gap-2" onClick={fetchDashboard} data-testid="sync-devices-btn"><RefreshCw className="w-4 h-4" /> Refresh</Button>
            <NotificationBell />
            <Button variant="outline" size="sm" onClick={() => setShowDeviceManager(true)} data-testid="manage-devices-btn"><Watch className="w-4 h-4" /></Button>
          </div>
        </div>

        {showReturnPrompt && !showTour && (
          <ReturnVisitPrompt
            onLogData={() => { setShowReturnPrompt(false); window.dispatchEvent(new CustomEvent('openHealthForm')); }}
            onSyncDevices={() => { setShowReturnPrompt(false); setShowDeviceManager(true); }}
            onDismiss={() => setShowReturnPrompt(false)}
          />
        )}

        {!hasData && !loading && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8 flex items-start gap-4" data-testid="no-data-banner">
            <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800 mb-1">No health data logged yet</h3>
              <p className="text-sm text-amber-700">Use the <strong>"Log Health Data"</strong> button in the header to start tracking your real health metrics. All dashboard values will update instantly.</p>
            </div>
            <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white gap-1 flex-shrink-0" onClick={() => window.dispatchEvent(new CustomEvent('openHealthForm'))} data-testid="log-data-cta-btn"><Plus className="w-4 h-4" /> Log Data Now</Button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-[#166534]" /></div>
        ) : (
          <>
            {/* Weekly Summary */}
            <WeeklySummary data={ws} />

            {/* Devices Bar */}
            <DevicesBar onManage={() => setShowDeviceManager(true)} />

            <Tabs defaultValue="overview" className="space-y-8">
              <TabsList className="bg-white border border-gray-200 p-1 rounded-xl">
                <TabsTrigger value="overview" className="rounded-lg" data-testid="tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="vitality" className="rounded-lg" data-testid="tab-vitality">Vitality Score</TabsTrigger>
                <TabsTrigger value="risks" className="rounded-lg">Health Risks</TabsTrigger>
                <TabsTrigger value="twin" className="rounded-lg">AI Digital Twin</TabsTrigger>
                <TabsTrigger value="insights" className="rounded-lg">AI Insights</TabsTrigger>
                <TabsTrigger value="labs" className="rounded-lg" data-testid="tab-labs">Lab Results</TabsTrigger>
              </TabsList>

              {/* ─── OVERVIEW ─── */}
              <TabsContent value="overview" className="space-y-8">
                <div className="grid lg:grid-cols-3 gap-8">
                  {/* Vitality */}
                  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="vitality-card">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900">Vitality Score</h3>
                      <span className="flex items-center gap-1 text-green-600 text-sm font-medium"><TrendingUp className="w-4 h-4" />{vs.trend}</span>
                    </div>
                    <div className="flex justify-center mb-4"><VitalityScoreCircle score={vs.current} size={180} /></div>
                    <div className="grid grid-cols-5 gap-2">
                      {vs.subscores.map(sub => (
                        <div key={sub.name} className="text-center">
                          <div className="text-lg font-bold" style={{ color: sub.color }}>{sub.score}</div>
                          <div className="text-xs text-gray-500">{sub.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Biological Age */}
                  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="bio-age-card">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900">Biological Age</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${ba.difference <= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {ba.difference <= 0 ? `${Math.abs(ba.difference)} years younger` : `${ba.difference} years older`}
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-8 mb-6">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-[#166534]">{ba.biological_age}</div>
                        <div className="text-sm text-gray-500">Bio Age</div>
                      </div>
                      <div className="text-2xl text-gray-300">vs</div>
                      <div className="text-center">
                        <div className="text-4xl font-bold text-gray-400">{ba.chronological_age}</div>
                        <div className="text-sm text-gray-500">Actual Age</div>
                      </div>
                    </div>
                    <div className="mb-2">
                      <div className="flex justify-between text-sm mb-1"><span className="text-gray-500">Longevity Score</span><span className="font-medium">{ba.longevity_score}/100</span></div>
                      <Progress value={ba.longevity_score} className="h-2" />
                    </div>
                    {ba.history.length > 0 && <MiniChart data={ba.history.map(h => h.age)} color="#166534" height={50} />}
                  </div>

                  {/* Risk Radar */}
                  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="risk-radar-card">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900">Health Risk Radar</h3>
                      <Shield className="w-5 h-5 text-[#166534]" />
                    </div>
                    <RiskRadarChart data={rr} />
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4" data-testid="quick-stats-row">
                  <div className="bg-white rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center gap-2 mb-2"><Heart className="w-5 h-5 text-red-500" /><span className="text-sm text-gray-500">Heart Rate</span></div>
                    <div className="text-2xl font-bold text-gray-900" data-testid="stat-heart-rate">{hr}</div>
                    <div className="text-xs text-gray-400">bpm</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center gap-2 mb-2"><Footprints className="w-5 h-5 text-green-500" /><span className="text-sm text-gray-500">Steps</span></div>
                    <div className="text-2xl font-bold text-gray-900" data-testid="stat-steps">{steps}</div>
                    <div className="text-xs text-gray-400">today</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center gap-2 mb-2"><Moon className="w-5 h-5 text-purple-500" /><span className="text-sm text-gray-500">Sleep</span></div>
                    <div className="text-2xl font-bold text-gray-900" data-testid="stat-sleep">{sleepH}{sleepH !== '--' ? 'h' : ''}</div>
                    <div className="text-xs text-gray-400">{sleepQ}</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center gap-2 mb-2"><Droplets className="w-5 h-5 text-blue-500" /><span className="text-sm text-gray-500">Hydration</span></div>
                    <div className="text-2xl font-bold text-gray-900" data-testid="stat-hydration">{water}{water !== '--' ? '/8' : ''}</div>
                    <div className="text-xs text-gray-400">glasses</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center gap-2 mb-2"><Brain className="w-5 h-5 text-pink-500" /><span className="text-sm text-gray-500">Stress</span></div>
                    <div className="text-2xl font-bold text-gray-900" data-testid="stat-stress">{stress}{stress !== '--' ? '/10' : ''}</div>
                    <div className="text-xs text-gray-400">{stress !== '--' ? (stress <= 4 ? 'low' : stress <= 6 ? 'moderate' : 'high') : '--'}</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center gap-2 mb-2"><Zap className="w-5 h-5 text-amber-500" /><span className="text-sm text-gray-500">Energy</span></div>
                    <div className="text-2xl font-bold text-gray-900">{energy}{energy !== '--' ? '/10' : ''}</div>
                    <div className="text-xs text-gray-400">level</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center gap-2 mb-2"><Sparkles className="w-5 h-5 text-teal-500" /><span className="text-sm text-gray-500">Mood</span></div>
                    <div className="text-2xl font-bold text-gray-900 capitalize">{mood}</div>
                    <div className="text-xs text-gray-400">today</div>
                  </div>
                </div>

                {/* AI Insights & Daily Plan */}
                <div className="grid lg:grid-cols-2 gap-8">
                  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#166534]" /> AI Daily Health Plan</h3>
                      <span className="text-sm text-gray-500">{ins.daily_health_plan.filter(a => a.completed).length}/{ins.daily_health_plan.length} completed</span>
                    </div>
                    <div className="space-y-3">
                      {ins.daily_health_plan.map((item, i) => (
                        <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${item.completed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-100 hover:border-[#166534]'}`}>
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${item.completed ? 'bg-green-500' : 'bg-gray-200'}`}>
                            {item.completed ? <CheckCircle2 className="w-4 h-4 text-white" /> : <span className="w-2 h-2 bg-gray-400 rounded-full" />}
                          </div>
                          <p className={`flex-1 text-sm ${item.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{item.action}</p>
                          <span className="text-xs text-gray-400">{item.time}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-6"><Brain className="w-5 h-5 text-[#166534]" /> AI Coaching Insights</h3>
                    {ins.alerts.length > 0 && (
                      <div className="mb-4 space-y-2">
                        {ins.alerts.map(alert => (
                          <div key={alert.id} className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl">
                            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <div><p className="text-sm text-red-800">{alert.message}</p><span className="text-xs text-red-500">{alert.time}</span></div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="space-y-3">
                      {ins.coaching_insights.map(ci => (
                        <div key={ci.id} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                          <div className={`w-2 h-2 rounded-full mt-2 ${ci.priority === 'high' ? 'bg-red-500' : ci.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'}`} />
                          <div className="flex-1"><p className="text-sm text-gray-800">{ci.message}</p><span className="text-xs text-gray-400">{ci.category}</span></div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 p-4 bg-gradient-to-r from-[#166534] to-[#14532d] rounded-xl text-white">
                      <div className="flex items-center justify-between mb-2"><span className="font-medium">{programData.activeProgram}</span><span className="text-sm text-green-200">{programData.progress}%</span></div>
                      <Progress value={programData.progress} className="h-2 bg-white/20" />
                      <p className="text-xs text-green-200 mt-2">Day {programData.daysCompleted} of {programData.totalDays}</p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* ─── VITALITY SCORE ─── */}
              <TabsContent value="vitality">
                <div className="bg-white rounded-2xl p-8 border border-gray-100">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Vitality Score Engine</h2>
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div className="flex flex-col items-center">
                      <VitalityScoreCircle score={vs.current} size={250} />
                      <p className="mt-4 text-gray-600 text-center max-w-md">Calculated from 5 key dimensions. Updated instantly when you log new health data.</p>
                    </div>
                    <div className="space-y-4">
                      {vs.subscores.map(sub => (
                        <div key={sub.name} className="p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">{sub.name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-2xl font-bold" style={{ color: sub.color }}>{sub.score}</span>
                              <span className={`text-sm ${String(sub.trend).startsWith('+') && sub.trend !== '+0' ? 'text-green-600' : String(sub.trend).startsWith('-') ? 'text-red-600' : 'text-gray-400'}`}>{sub.trend}</span>
                            </div>
                          </div>
                          <Progress value={sub.score} className="h-2" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* ─── HEALTH RISKS ─── */}
              <TabsContent value="risks">
                <div className="bg-white rounded-2xl p-8 border border-gray-100">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Health Risk Radar</h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    {Object.entries(rr).map(([key, data]) => (
                      <div key={key} className="p-6 bg-gray-50 rounded-2xl">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${data.risk === 'low' ? 'bg-green-100 text-green-700' : data.risk === 'moderate' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{data.risk} risk</span>
                        </div>
                        <div className="mb-4">
                          <div className="flex justify-between text-sm mb-1"><span className="text-gray-500">Score</span><span className="font-medium">{data.score}/100</span></div>
                          <Progress value={data.score} className="h-3" />
                        </div>
                        <div className="space-y-2">
                          {data.factors.map((f, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle2 className="w-4 h-4 text-green-500" /> {f}</div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* ─── AI DIGITAL TWIN ─── */}
              <TabsContent value="twin">
                <div className="bg-white rounded-2xl p-8 border border-gray-100">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#166534] to-[#22C55E] rounded-xl flex items-center justify-center"><Sparkles className="w-6 h-6 text-white" /></div>
                    <div><h2 className="text-2xl font-bold text-gray-900">AI Digital Twin</h2><p className="text-gray-500">Predictive health modeling based on your data</p></div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-6">
                    {preds.map(p => (
                      <div key={p.id} className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-sm font-medium text-[#166534] bg-green-50 px-3 py-1 rounded-full">{p.scenario}</span>
                          <span className="text-sm text-gray-400">{p.confidence}%</span>
                        </div>
                        <p className="text-gray-700 mb-4">{p.prediction}</p>
                        <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-500" /><span className="text-sm text-green-600">Positive impact</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* ─── AI INSIGHTS ─── */}
              <TabsContent value="insights">
                <div className="bg-white rounded-2xl p-8 border border-gray-100">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">AI Insight & Prescription Engine</h2>
                  <p className="text-gray-600 mb-8">Personalized recommendations generated from your logged health data.</p>
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-4">Today's Health Plan</h3>
                      <div className="space-y-3">
                        {ins.daily_health_plan.map((item, i) => (
                          <div key={i} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                            <Clock className="w-5 h-5 text-gray-400" />
                            <span className="text-sm font-medium text-gray-500 w-20">{item.time}</span>
                            <span className="text-sm text-gray-800 flex-1">{item.action}</span>
                            {item.completed && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-4">Coaching Recommendations</h3>
                      <div className="space-y-3">
                        {ins.coaching_insights.map(ci => (
                          <div key={ci.id} className="p-4 bg-gray-50 rounded-xl">
                            <p className="text-gray-800 mb-2">{ci.message}</p>
                            <span className="text-xs text-gray-400 bg-gray-200 px-2 py-1 rounded">{ci.category}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* ─── LAB RESULTS ─── */}
              <TabsContent value="labs">
                <LabResultsTab />
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
      <DeviceManager isOpen={showDeviceManager} onClose={() => setShowDeviceManager(false)} />
      <Footer />
    </div>
  );
};

export default HealthDashboardPage;
